import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  Typography,
  Modal,
} from '@mui/material';

const ImageContainer = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
    '& .MuiCardMedia-root': {
      filter: 'brightness(0.9)',
    },
    '& .action-buttons': {
      opacity: 1,
    },
  },
}));

const ImageOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  '&:hover': {
    opacity: 1,
  },
}));

const ActionButtons = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '90vw',
  maxHeight: '90vh',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[24],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const ModalImage = styled('img')({
  maxWidth: '100%',
  maxHeight: 'calc(90vh - 100px)',
  objectFit: 'contain',
});

const ImageGrid = ({
  images,
  loading,
  folder,
  actionIcon,
  actionTooltip,
  actionLabel,
  onActionClick,
  secondaryActionIcon,
  secondaryActionTooltip,
  secondaryActionLabel,
  onSecondaryActionClick,
  tertiaryActionIcon,
  tertiaryActionTooltip,
  tertiaryActionLabel,
  onTertiaryActionClick,
  onImageClick,
  showCaptions = false,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleImageClick = (image) => {
    if (onImageClick) {
      onImageClick(image.id);
    } else {
      setSelectedImage(image);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!images || images.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography variant="body1" color="textSecondary">
          No images found in this folder.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        {images.map((image) => (
          <Grid item xs={12} sm={6} md={3} key={image.id}>
            <ImageContainer>
              <CardMedia
                component="img"
                height="200"
                image={image.url}
                alt={image.caption || 'Image'}
                loading="lazy"
                sx={{ cursor: 'pointer' }}
                onClick={() => handleImageClick(image)}
              />
              <ImageOverlay onClick={() => handleImageClick(image)} />
              
              {/* Caption overlay at the bottom */}
              {showCaptions && image.caption && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: 1,
                    fontSize: '0.75rem',
                    maxHeight: '60px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {image.caption}
                </Box>
              )}
              <ActionButtons className="action-buttons">
                {actionIcon && (
                  actionLabel ? (
                    <Tooltip title={actionTooltip || ''} placement="left">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={actionIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onActionClick(image.id, folder);
                        }}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', color: 'primary.main' }}
                      >
                        {actionLabel}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title={actionTooltip || ''} placement="left">
                      <StyledIconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onActionClick(image.id, folder);
                        }}
                      >
                        {actionIcon}
                      </StyledIconButton>
                    </Tooltip>
                  )
                )}
                {secondaryActionIcon && (
                  secondaryActionLabel ? (
                    <Tooltip title={secondaryActionTooltip || ''} placement="left">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={secondaryActionIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSecondaryActionClick(image.id, folder);
                        }}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', color: 'secondary.main', mt: 1 }}
                      >
                        {secondaryActionLabel}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title={secondaryActionTooltip || ''} placement="left">
                      <StyledIconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSecondaryActionClick(image.id, folder);
                        }}
                      >
                        {secondaryActionIcon}
                      </StyledIconButton>
                    </Tooltip>
                  )
                )}
                {tertiaryActionIcon && (
                  tertiaryActionLabel ? (
                    <Tooltip title={tertiaryActionTooltip || ''} placement="left">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={tertiaryActionIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onTertiaryActionClick(image.id, folder);
                        }}
                        sx={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                          color: 'info.main', 
                          mt: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                          }
                        }}
                      >
                        {tertiaryActionLabel}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title={tertiaryActionTooltip || ''} placement="left">
                      <StyledIconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTertiaryActionClick(image.id, folder);
                        }}
                      >
                        {tertiaryActionIcon}
                      </StyledIconButton>
                    </Tooltip>
                  )
                )}
              </ActionButtons>
            </ImageContainer>
          </Grid>
        ))}
      </Grid>

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="image-modal-title"
        aria-describedby="image-modal-description"
      >
        <ModalContent>
          {selectedImage && (
            <>
              <ModalImage
                src={selectedImage.url}
                alt={selectedImage.caption || 'Image'}
              />
              {selectedImage.caption && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {selectedImage.caption}
                </Typography>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

ImageGrid.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      caption: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool,
  folder: PropTypes.string.isRequired,
  actionIcon: PropTypes.node,
  actionTooltip: PropTypes.string,
  actionLabel: PropTypes.string,
  onActionClick: PropTypes.func,
  secondaryActionIcon: PropTypes.node,
  secondaryActionTooltip: PropTypes.string,
  secondaryActionLabel: PropTypes.string,
  onSecondaryActionClick: PropTypes.func,
  tertiaryActionIcon: PropTypes.node,
  tertiaryActionTooltip: PropTypes.string,
  tertiaryActionLabel: PropTypes.string,
  onTertiaryActionClick: PropTypes.func,
  onImageClick: PropTypes.func,
  showCaptions: PropTypes.bool,
};

ImageGrid.defaultProps = {
  loading: false,
  actionIcon: null,
  actionTooltip: '',
  actionLabel: null,
  onActionClick: () => {},
  secondaryActionIcon: null,
  secondaryActionTooltip: '',
  secondaryActionLabel: null,
  onSecondaryActionClick: () => {},
  tertiaryActionIcon: null,
  tertiaryActionTooltip: '',
  tertiaryActionLabel: null,
  onTertiaryActionClick: () => {},
  onImageClick: null,
  showCaptions: false,
};

export default ImageGrid;
